/**
 * Preview mode (closed / flat) slider and side (front / back) toggles.
 *
 * Default is Product view (`closed`). Flat faces is only a fallback after the
 * preview payload confirms this SKU has no mockup — not while boot still has
 * `supportsMockup: false` as the unknown/unresolved state.
 */

/**
 * @param {{ mockupResolved?: unknown, supportsMockup?: unknown, previewMode?: unknown }} state
 * @returns {boolean}
 */
export function shouldFallbackToFlatPreview(state) {
    return Boolean(state.mockupResolved) && !state.supportsMockup && state.previewMode === 'closed';
}

/**
 * Decide previewMode after a mockup payload (or confirmed miss).
 * Returns null when the current mode should be left unchanged (user choice).
 *
 * @param {{ mockupResolved?: unknown, previewMode?: unknown }} prev
 * @param {boolean} supportsMockup
 * @returns {'closed'|'flat'|null}
 */
export function previewModeAfterMockupPayload(prev, supportsMockup) {
    const current = prev.previewMode === 'flat' ? 'flat' : 'closed';
    if (!supportsMockup) {
        return 'flat';
    }
    if (!prev.mockupResolved && current === 'flat') {
        return 'closed';
    }
    return null;
}

/**
 * @param {HTMLElement} root
 * @param {{ setState: (patch: Record<string, unknown>) => void, getState: () => Record<string, unknown> }} store
 */
export function bindStudioControls(root, store) {
    const slider = root.querySelector('[data-bag-preview-mode-slider]');

    if (slider instanceof HTMLInputElement) {
        slider.addEventListener('input', () => {
            if (store.getState().busy) {
                return;
            }
            const mode = slider.value === '1' ? 'closed' : 'flat';
            store.setState({ previewMode: mode });
        });
    }

    root.querySelectorAll('[data-bag-preview-mode]').forEach((btn) => {
        btn.addEventListener('click', () => {
            const mode = btn.getAttribute('data-bag-preview-mode');
            if (mode !== 'closed' && mode !== 'flat') {
                return;
            }
            store.setState({ previewMode: mode });
        });
    });

    root.querySelectorAll('[data-bag-side]').forEach((btn) => {
        btn.addEventListener('click', () => {
            if (store.getState().busy) {
                return;
            }
            const side = btn.getAttribute('data-bag-side');
            if (side !== 'front' && side !== 'back') {
                return;
            }
            store.setState({ activeSide: side });
        });
    });

    store.subscribe((state) => {
        const previewMode = state.previewMode === 'flat' ? 'flat' : 'closed';
        const supportsMockup = Boolean(state.supportsMockup);

        if (slider instanceof HTMLInputElement) {
            slider.disabled = !supportsMockup || Boolean(state.busy);
            slider.value = previewMode === 'closed' ? '1' : '0';
            slider.setAttribute(
                'aria-valuetext',
                previewMode === 'closed' ? 'Product view' : 'Flat faces',
            );
        }

        root.querySelectorAll('[data-bag-preview-mode]').forEach((btn) => {
            const mode = btn.getAttribute('data-bag-preview-mode');
            btn.classList.toggle('is-active', mode === previewMode);
            btn.setAttribute('aria-pressed', mode === previewMode ? 'true' : 'false');
        });

        root.querySelectorAll('[data-bag-preview-mode-slider-wrap]').forEach((wrap) => {
            wrap.classList.toggle('is-disabled', !supportsMockup);
        });

        root.querySelectorAll('[data-bag-side]').forEach((btn) => {
            const side = btn.getAttribute('data-bag-side');
            btn.classList.toggle('is-active', side === state.activeSide);
            btn.setAttribute('aria-pressed', side === state.activeSide ? 'true' : 'false');
            if (btn instanceof HTMLButtonElement) {
                btn.disabled = Boolean(state.busy);
            }
        });

        const sideGroup = root.querySelector('[data-bag-side-group]');
        if (sideGroup instanceof HTMLElement) {
            const showSideToggle = previewMode === 'closed';
            sideGroup.classList.toggle('d-none', !showSideToggle);
            sideGroup.setAttribute('aria-hidden', showSideToggle ? 'false' : 'true');
        }
    });
}
